import { memo, useCallback, useEffect, useMemo } from "react";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import {
  tableActionHeaderCellStyles,
  tableHeaderCellStyles,
} from "@/components/GenericTable/styles";

import { patientIdFromReference } from "../aggregations";
import { useSetSelection } from "../hooks/useSetSelection";
import { useTablePagination } from "../hooks/useTablePagination";
import CommunicationsTablePagination from "./CommunicationsTablePagination";
import {
  compareCommunicationsDateTimeDesc,
  formatCommunicationsDateTimeDisplay,
} from "../helpers/formatCrmCommunicationsDisplay";
import { formatPatientListLabel } from "../patientLabels";
import PatientNameCell from "./PatientNameCell";
import {
  PatientBirthDateTableCellContent,
  PatientContactTableCellContent,
  PatientTaxIdentifierTableCellContent,
} from "./PatientSummaryTableCells";
import type { CrmPatientSummary, FluxZapCommunicationRecord } from "../types";
import type { OnOpenCommunicationsWhatsAppSend } from "../types/sendMessage";
import CommunicationPatientSelectionBar from "./CommunicationPatientSelectionBar";
import {
  communicationsRankRowSx,
  communicationsRankTableBodyCellSx,
} from "./communicationsSubsectionStyles";

/** Payload do diálogo de detalhes: todos os envios do modelo para o destinatário. */
export interface TemplateSendDetailPayload {
  messages: FluxZapCommunicationRecord[];
  patient: CrmPatientSummary | undefined;
}

export interface CommunicationsTemplateSendRowsProps {
  selectedTemplateMessages: FluxZapCommunicationRecord[];
  selectedTemplateCode: string | null;
  selectedTemplateLabel: string | null;
  selectedTemplateSendCount?: number;
  vetApp?: boolean;
  petNamesByTutorLogicalId?: Map<string, string[]>;
  resolvePatientForMessage: (
    msg: FluxZapCommunicationRecord,
  ) => CrmPatientSummary | undefined;
  onClearSelection: () => void;
  onOpenSendDetail: (payload: TemplateSendDetailPayload) => void;
  onOpenSendMessage: OnOpenCommunicationsWhatsAppSend;
}

function destinationKey(
  msg: FluxZapCommunicationRecord,
  patient: CrmPatientSummary | undefined,
): string {
  return patient?.patientId ?? `orphan:${msg.id}`;
}

function labelForDestination(
  msg: FluxZapCommunicationRecord,
  patient: CrmPatientSummary | undefined,
): string {
  if (patient) return formatPatientListLabel(patient);
  return msg.subjectPatientReference;
}

interface TemplateSendRowView {
  msg: FluxZapCommunicationRecord;
  patient: CrmPatientSummary | undefined;
  destId: string;
  /** Quantidade de envios deste modelo para o mesmo destinatário. */
  sendCount: number;
  /** Todos os envios deste modelo para o destinatário (mais recente primeiro). */
  messages: FluxZapCommunicationRecord[];
}

function crmPatientFromTemplateRow(
  row: TemplateSendRowView,
): CrmPatientSummary {
  if (row.patient) return row.patient;
  const fromRef = patientIdFromReference(row.msg.subjectPatientReference);
  const patientId = fromRef ?? row.destId.replace(/^orphan:/, "");
  return {
    patientId,
    officialName: row.msg.subjectPatientReference,
    birthDate: "",
  };
}

/**
 * Um registro por paciente: mantém o Basic mais recente e soma envios repetidos.
 */
function buildUniqueTemplateSendRows(
  messages: FluxZapCommunicationRecord[],
  resolvePatientForMessage: (
    msg: FluxZapCommunicationRecord,
  ) => CrmPatientSummary | undefined,
): TemplateSendRowView[] {
  const byDest = new Map<string, TemplateSendRowView>();

  for (const msg of messages) {
    const patient = resolvePatientForMessage(msg);
    const destId = destinationKey(msg, patient);
    const existing = byDest.get(destId);

    if (!existing) {
      byDest.set(destId, {
        msg,
        patient,
        destId,
        sendCount: 1,
        messages: [msg],
      });
      continue;
    }

    existing.sendCount += 1;
    existing.messages.push(msg);
    if (compareCommunicationsDateTimeDesc(existing.msg.created, msg.created) > 0) {
      existing.msg = msg;
      existing.patient = patient;
    }
  }

  for (const row of byDest.values()) {
    row.messages.sort((a, b) =>
      compareCommunicationsDateTimeDesc(a.created, b.created),
    );
  }

  return [...byDest.values()].sort((a, b) =>
    compareCommunicationsDateTimeDesc(a.msg.created, b.msg.created),
  );
}

/** Linhas de envios para o modelo selecionado: seleção por paciente + envio WhatsApp. */
const CommunicationsTemplateSendRows = memo(
  ({
    selectedTemplateMessages,
    selectedTemplateCode,
    selectedTemplateLabel,
    selectedTemplateSendCount,
    vetApp = false,
    petNamesByTutorLogicalId,
    resolvePatientForMessage,
    onClearSelection,
    onOpenSendDetail,
    onOpenSendMessage,
  }: CommunicationsTemplateSendRowsProps) => {
    const { selectedIds, selectedCount, toggle, toggleAll, clear, has } =
      useSetSelection();

    const rows = useMemo(
      () =>
        buildUniqueTemplateSendRows(
          selectedTemplateMessages,
          resolvePatientForMessage,
        ),
      [selectedTemplateMessages, resolvePatientForMessage],
    );

    const uniqueDestIds = useMemo(() => rows.map((r) => r.destId), [rows]);

    const templateRowsResetKey = useMemo(
      () =>
        rows.length > 0 ? `${rows.length}-${rows[0]?.destId ?? ""}` : "empty",
      [rows],
    );

    const {
      paginatedItems: paginatedRows,
      page,
      rowsPerPage,
      totalCount: templateRowCount,
      handlePageChange,
      handleRowsPerPageChange,
    } = useTablePagination(rows, templateRowsResetKey);

    const allSelected =
      uniqueDestIds.length > 0 &&
      uniqueDestIds.every((id) => selectedIds.has(id));
    const someSelected = uniqueDestIds.some((id) => selectedIds.has(id));

    useEffect(() => {
      clear();
    }, [selectedTemplateMessages, clear]);

    const openSendForDestIds = useCallback(
      (ids: Iterable<string>) => {
        const patients: CrmPatientSummary[] = [];
        for (const id of ids) {
          const row = rows.find((r) => r.destId === id);
          if (row) patients.push(crmPatientFromTemplateRow(row));
        }
        if (patients.length > 0) {
          onOpenSendMessage({
            patients,
            templateCode: selectedTemplateCode,
          });
        }
      },
      [onOpenSendMessage, rows, selectedTemplateCode],
    );

    return (
      <Stack spacing={1}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexWrap="wrap"
          gap={1}
        >
          <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Envios do modelo selecionado
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {selectedTemplateLabel ?? selectedTemplateCode}
              </Typography>
              {selectedTemplateSendCount != null ? (
                <Chip
                  size="small"
                  label={`${selectedTemplateSendCount} envio(s)`}
                />
              ) : null}
            </Stack>
          </Stack>
          <Button size="small" onClick={onClearSelection} sx={{ flexShrink: 0 }}>
            Limpar seleção do modelo
          </Button>
        </Stack>

        <CommunicationPatientSelectionBar
          selectedCount={selectedCount}
          onSendMessage={() => openSendForDestIds(selectedIds)}
          onClearSelection={clear}
        />

        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="Pacientes por modelo">
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={tableHeaderCellStyles}
                  align="center"
                >
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={() => toggleAll(uniqueDestIds)}
                    inputProps={{
                      "aria-label":
                        "Selecionar todos os destinatários desta lista",
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={tableHeaderCellStyles}
                  align="center"
                >
                  <Typography variant="subtitle2">Paciente</Typography>
                </TableCell>
                <TableCell
                  sx={tableHeaderCellStyles}
                  align="center"
                >
                  <Typography variant="subtitle2">Contato</Typography>
                </TableCell>
                {!vetApp ? (
                  <TableCell sx={tableHeaderCellStyles} align="center">
                    <Typography variant="subtitle2">Nascimento</Typography>
                  </TableCell>
                ) : (
                  <TableCell sx={tableHeaderCellStyles} align="center">
                    <Typography variant="subtitle2">Pet(s)</Typography>
                  </TableCell>
                )}
                <TableCell
                  sx={tableHeaderCellStyles}
                  align="center"
                >
                  <Typography variant="subtitle2">CPF</Typography>
                </TableCell>
                <TableCell
                  sx={tableHeaderCellStyles}
                  align="center"
                >
                  <Typography variant="subtitle2">Enviado em</Typography>
                </TableCell>
                <TableCell
                  sx={tableActionHeaderCellStyles}
                  align="center"
                >
                  <Typography variant="subtitle2">Ações</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map(({ msg, patient, destId, sendCount, messages }) => {
                const checked = has(destId);
                return (
                  <TableRow
                    key={destId}
                    hover
                    selected={checked}
                    sx={communicationsRankRowSx(checked)}
                  >
                    <TableCell
                      padding="checkbox"
                      align="center"
                      sx={communicationsRankTableBodyCellSx}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={() => toggle(destId)}
                        inputProps={{
                          "aria-label": `Selecionar ${labelForDestination(msg, patient)}`,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={communicationsRankTableBodyCellSx}
                    >
                      {patient ? (
                        <Tooltip title={formatPatientListLabel(patient)}>
                          <Box
                            sx={{
                              maxWidth: { xs: 140, sm: 220, md: 280 },
                              mx: "auto",
                              textAlign: "center",
                            }}
                          >
                            <PatientNameCell
                              patient={patient}
                              primaryVariant="body2"
                              primaryFontWeight={400}
                            />
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2">—</Typography>
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={communicationsRankTableBodyCellSx}
                    >
                      <PatientContactTableCellContent patient={patient} />
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={communicationsRankTableBodyCellSx}
                    >
                      {!vetApp ? (
                        <PatientBirthDateTableCellContent patient={patient} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {petNamesByTutorLogicalId
                            ?.get(patient?.patientId ?? "")
                            ?.join(", ") || "—"}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={communicationsRankTableBodyCellSx}
                    >
                      <PatientTaxIdentifierTableCellContent patient={patient} />
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={communicationsRankTableBodyCellSx}
                    >
                      <Typography variant="body2">
                        {formatCommunicationsDateTimeDisplay(msg.created)}
                      </Typography>
                      {sendCount > 1 ? (
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          {sendCount} envios deste modelo
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={communicationsRankTableBodyCellSx}
                    >
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Tooltip
                          title={
                            sendCount > 1
                              ? `Ver detalhes dos ${sendCount} envios`
                              : "Ver detalhes do envio"
                          }
                        >
                          <IconButton
                            size="small"
                            aria-label={
                              sendCount > 1
                                ? `Ver detalhes dos ${sendCount} envios`
                                : "Ver detalhes do envio"
                            }
                            onClick={() =>
                              onOpenSendDetail({ messages, patient })
                            }
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Enviar mensagem via WhatsApp">
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label={`Enviar mensagem via WhatsApp para ${labelForDestination(msg, patient)}`}
                            onClick={() => openSendForDestIds([destId])}
                          >
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <CommunicationsTablePagination
          count={templateRowCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Stack>
    );
  },
);

CommunicationsTemplateSendRows.displayName = "CommunicationsTemplateSendRows";

export default CommunicationsTemplateSendRows;
